import { ReloadOutlined } from '@ant-design/icons';
import type { ItemType, MenuItemGroupType, MenuItemType } from 'antd/es/menu/interface';
import type { TFunction } from 'i18next';
import type { HTMLAttributes, KeyboardEvent } from 'react';

import { ADVANCED_MENU_ITEM_ATTRIBUTE } from '../utils';

import { UserTemplateOptionItem } from './UserTemplateOptionItem';
import {
  type UserTemplateOptionFields,
  type UserTemplateOptionKeyboardAction,
  getUserOptionMenuKey,
  getUserTemplateOptionAccessibleName,
  getUserTemplateOptionDescription,
  getUserTemplateOptionItemClassName,
  getUserTemplateOptionKeyboardAction,
  getUserTemplateOptionKeyShortcuts,
  isShareInHorizonOptionDisabled,
  isUserTemplateOptionShortcut,
  PINNED_MENU_KEY,
  RECENTLY_USED_MENU_KEY,
  USER_TEMPLATE_OPTIONS_RETRY_MENU_KEY,
} from './utils';

type ActionHandler = () => void | Promise<void>;

type BuildUserTemplateOptionMenuItemsArgs = {
  pinned: UserTemplateOptionFields[];
  recent: UserTemplateOptionFields[];
  t: TFunction;
  shareInHorizonOrganizationCount: number;
  getDisabledReason?: (option: UserTemplateOptionFields) => string | undefined;
  onPin: (option: UserTemplateOptionFields) => void | Promise<void>;
  onUnpin: (option: UserTemplateOptionFields) => void | Promise<void>;
  onMove: (index: number, direction: 'up' | 'down') => void | Promise<void>;
  highlightedOptionId?: string | null;
  highlightToken?: number;
  loadFailed?: boolean;
};

// rc-menu spreads any extra item props onto its <li>; antd's item type just does
// not list the ones the row's keyboard path needs. `aria-description` (ARIA 1.3)
// postdates the React 17 typings.
type OptionMenuItem = MenuItemType &
  Pick<
    HTMLAttributes<HTMLLIElement>,
    'onKeyDown' | 'onKeyDownCapture' | 'tabIndex' | 'aria-keyshortcuts'
  > & {
    'aria-description': string;
    'aria-label': string;
    'data-user-option-id': string;
  };

// The row an option currently renders as, wherever it sits in `menu`.
const findOptionRow = (menu: Element | null, optionId: string) =>
  Array.from(menu?.querySelectorAll<HTMLElement>('[data-user-option-id]') ?? []).find(
    (element) => element.dataset.userOptionId === optionId
  );

const buildOptionItem = ({
  option,
  pinned,
  isFirst,
  isLast,
  disabled,
  disabledReason,
  description,
  accessibleName,
  onPin,
  onUnpin,
  onMoveUp,
  onMoveDown,
  highlighted,
  highlightToken = 0,
}: {
  option: UserTemplateOptionFields;
  pinned: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  disabled: boolean;
  disabledReason?: string;
  description: string;
  accessibleName: string;
  onPin: (option: UserTemplateOptionFields) => void | Promise<void>;
  onUnpin: (option: UserTemplateOptionFields) => void | Promise<void>;
  onMoveUp?: ActionHandler;
  onMoveDown?: ActionHandler;
  highlighted: boolean;
  highlightToken?: number;
}): OptionMenuItem => {
  const item = (
    <UserTemplateOptionItem
      option={option}
      pinned={pinned}
      isFirst={isFirst}
      isLast={isLast}
      disabledReason={disabledReason}
      highlighted={highlighted}
      highlightToken={highlightToken}
      onPin={() => onPin(option)}
      onUnpin={() => onUnpin(option)}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
    />
  );

  const actions: Record<UserTemplateOptionKeyboardAction, ActionHandler | undefined> = {
    pin: () => onPin(option),
    unpin: () => onUnpin(option),
    moveUp: onMoveUp,
    moveDown: onMoveDown,
  };

  // Focus sits on the <li>, so the row's shortcuts are handled here rather than in
  // the label component, which never sees the key.
  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (!isUserTemplateOptionShortcut(event)) {
      return;
    }
    // The row's, not rc-menu's: left to bubble, Alt+Arrow also walks the menu.
    event.preventDefault();
    event.stopPropagation();

    const action = getUserTemplateOptionKeyboardAction(event, { pinned, isFirst, isLast });
    const run = action ? actions[action] : undefined;
    if (!run) {
      return;
    }

    const row = event.currentTarget;
    // The menu outlives the action; the row may not (below).
    const menu = row.closest('[role="menu"]');
    Promise.resolve(run()).then(() => {
      // Moves and section changes remount the row and drop focus; refocus by option a frame later.
      window.requestAnimationFrame(() => {
        const next = row.isConnected ? row : findOptionRow(menu, option.id);
        if (next) {
          next.focus();
          return;
        }
        // The option is gone. Stay inside the menu, on Advanced.
        menu?.querySelector<HTMLElement>(`[${ADVANCED_MENU_ITEM_ATTRIBUTE}]`)?.focus();
      });
    });
  };

  // rc-menu's Enter fires the item's click whether or not the item is disabled,
  // and the Dropdown closes on it. Stopped in the capture phase, before rc-menu's
  // own handler runs, so Enter on a disabled row does what a click on it does:
  // nothing, with the menu still open.
  const handleKeyDownCapture = (event: KeyboardEvent<HTMLLIElement>) => {
    if (disabled && event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return {
    key: getUserOptionMenuKey(option.id),
    className: getUserTemplateOptionItemClassName({ pinned, highlighted }),
    // Disabled keeps the greyed look, aria-disabled and rc-menu's click guard; only
    // running the report is off the table. rc-menu also drops a disabled item's
    // tabindex, which would take the row out of the arrow-key order and, with it,
    // the shortcuts above -- the only keyboard path to its pin controls. A pinned
    // row that cannot run would then hold its slot for good. Item props land after
    // rc-menu's own, so this puts the tabindex back.
    disabled,
    tabIndex: -1,
    label: item,
    onKeyDown: handleKeyDown,
    onKeyDownCapture: handleKeyDownCapture,
    'aria-keyshortcuts': getUserTemplateOptionKeyShortcuts(pinned),
    'aria-description': description,
    'aria-label': accessibleName,
    'data-user-option-id': option.id,
  };
};

/**
 * The Pinned and Recently Used sections of the Export Report menu: menu groups, like
 * the per-context template groups above them, so their rows are on screen as soon
 * as the menu opens. Each section is left out when it has no rows. How many rows a
 * section holds is the service's setting, not the client's, so the lists are
 * rendered as returned.
 */
export const buildUserTemplateOptionMenuItems = ({
  pinned,
  recent,
  t,
  shareInHorizonOrganizationCount,
  getDisabledReason,
  onPin,
  onUnpin,
  onMove,
  highlightedOptionId = null,
  highlightToken = 0,
  loadFailed = false,
}: BuildUserTemplateOptionMenuItemsArgs): ItemType[] => {
  // Without this a failed load is indistinguishable from "nothing pinned yet". One
  // enabled row that retries in place; the hook decides when the failure is shown.
  if (loadFailed) {
    return [
      {
        key: USER_TEMPLATE_OPTIONS_RETRY_MENU_KEY,
        icon: <ReloadOutlined />,
        label: t(
          'reports.retryLoadingUserTemplateOptions',
          'Retry loading pinned and recent reports'
        ),
      },
    ];
  }

  const resolveDisabledReason = (option: UserTemplateOptionFields) => {
    if (isShareInHorizonOptionDisabled(option, shareInHorizonOrganizationCount)) {
      return t(
        'reports.noOrgsForShareInHorizon',
        'No Task Organizations are available for Share in Horizon delivery.'
      );
    }
    return getDisabledReason?.(option);
  };

  const sections: MenuItemGroupType[] = [];

  if (pinned.length) {
    sections.push({
      type: 'group',
      key: PINNED_MENU_KEY,
      label: t('reports.pinned', 'Pinned'),
      children: pinned.map((option, index) => {
        const disabledReason = resolveDisabledReason(option);
        return buildOptionItem({
          option,
          pinned: true,
          isFirst: index === 0,
          isLast: index === pinned.length - 1,
          disabled: !!disabledReason,
          disabledReason,
          description: getUserTemplateOptionDescription(t, { pinned: true, disabledReason }),
          accessibleName: getUserTemplateOptionAccessibleName(t, option, true),
          onPin,
          onUnpin,
          onMoveUp: () => onMove(index, 'up'),
          onMoveDown: () => onMove(index, 'down'),
          highlighted: highlightedOptionId === option.id,
          highlightToken,
        });
      }),
    });
  }

  if (recent.length) {
    sections.push({
      type: 'group',
      key: RECENTLY_USED_MENU_KEY,
      label: t('reports.recentlyUsed', 'Recently Used'),
      children: recent.map((option) => {
        const disabledReason = resolveDisabledReason(option);
        return buildOptionItem({
          option,
          pinned: false,
          disabled: !!disabledReason,
          disabledReason,
          description: getUserTemplateOptionDescription(t, { pinned: false, disabledReason }),
          accessibleName: getUserTemplateOptionAccessibleName(t, option, false),
          onPin,
          onUnpin,
          highlighted: highlightedOptionId === option.id,
          highlightToken,
        });
      }),
    });
  }

  return sections;
};
